import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import i18n from "../i18n";
import { authApi, type MeResponse, type UserSummary } from "../api/auth";
import { resetSessionExpiredNotice, setSessionExpiredHandler, setAccessPolicyChangedHandler,
  setKnownAccessPolicyVersion, type ApiError } from "../api/client";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: UserSummary; permissions: Set<string> };

type AuthContextValue = {
  state: AuthState;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
    totpCode?: string,
  ) => Promise<{ mfaRequired: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  // Mirror of state.status for the session-expired handler — it runs outside
  // React's render cycle and must see the CURRENT status, not a stale closure.
  const statusRef = useRef<AuthState["status"]>("loading");
  const sessionEpoch = useRef(0);
  const refreshInFlight = useRef<Promise<void> | null>(null);
  useEffect(() => { statusRef.current = state.status; }, [state.status]);

  // When a 401 survives a failed token refresh, the session is gone: flip to
  // anonymous so route guards redirect to /login instead of leaving dead
  // widgets polling 401s. Only acts when we believed we were logged in.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (statusRef.current !== "authenticated") return;
      // Flag for the login page so it can explain WHY the user landed there.
      try { sessionStorage.setItem("bankom.sessionExpired", "1"); } catch { /* ignore */ }
      sessionEpoch.current += 1;
      refreshInFlight.current = null;
      statusRef.current = "anonymous";
      setKnownAccessPolicyVersion(undefined);
      setState({ status: "anonymous" });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const refresh = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const epoch = sessionEpoch.current;
    const request = (async () => {
      try {
        const me: MeResponse = await authApi.me();
        if (epoch !== sessionEpoch.current) return;
        resetSessionExpiredNotice();
        setKnownAccessPolicyVersion(me.accessPolicyVersion);
        statusRef.current = "authenticated";
        setState({ status: "authenticated", user: me.user, permissions: new Set(me.permissions) });
        if (me.user.locale && i18n.resolvedLanguage !== me.user.locale) {
          void i18n.changeLanguage(me.user.locale);
        }
      } catch (error) {
        if (epoch !== sessionEpoch.current) return;
        // Keep the session on transient background failures; the server remains authoritative.
        if (statusRef.current !== "authenticated" || (error as ApiError)?.status === 401) {
          statusRef.current = "anonymous";
          setKnownAccessPolicyVersion(undefined);
          setState({ status: "anonymous" });
        }
      }
    })();
    refreshInFlight.current = request;
    try {
      await request;
    } finally {
      if (refreshInFlight.current === request) refreshInFlight.current = null;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const check = () => {
      if (statusRef.current === "authenticated" && !document.hidden) void refresh();
    };
    setAccessPolicyChangedHandler(check);
    const interval = window.setInterval(check, 30_000);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      setAccessPolicyChangedHandler(null);
      window.clearInterval(interval);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean, totpCode?: string) => {
      const res = await authApi.login(email, password, rememberMe, totpCode);
      if (res && typeof res === "object" && "mfaRequired" in res && res.mfaRequired) {
        return { mfaRequired: true } as const;
      }
      sessionEpoch.current += 1;
      refreshInFlight.current = null;
      await refresh();
      return { mfaRequired: false } as const;
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    sessionEpoch.current += 1;
    refreshInFlight.current = null;
    statusRef.current = "anonymous";
    setKnownAccessPolicyVersion(undefined);
    try {
      await authApi.logout();
    } finally {
      setState({ status: "anonymous" });
    }
  }, []);

  const hasPermission = useCallback(
    (permission: string) =>
      state.status === "authenticated" && state.permissions.has(permission),
    [state],
  );

  const hasRole = useCallback(
    (role: string) =>
      state.status === "authenticated" && state.user.roles.includes(role),
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refresh, hasPermission, hasRole }),
    [state, login, logout, refresh, hasPermission, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
